import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, ClipboardList, Loader2 } from "lucide-react";
import {
  SERVICE_TYPES,
  FUEL_LEVELS,
  CHECKLIST_LEVEL,
  BATTERY_LEVEL,
} from "@/lib/constants";

const EMPTY = {
  branch_id: "",
  customer_id: "",
  vehicle_id: "",
  received_by_user_id: "",
  intake_date: "",
  intake_time: "",
  service_type: "mantenimiento",
  service_flow_id: "",
  entry_reason: "",
  customer_reported_issue: "",
  mileage_at_entry: "",
  fuel_level: "half",
  general_condition_notes: "",
  accessories_notes: "",
  intake_signature_name: "",
  checklist: {
    exterior_status: "good",
    interior_status: "good",
    dashboard_lights: "",
    visible_leaks: false,
    battery_status: "good",
    tire_condition: "regular",
    accessories_present: "",
    notes: "",
  },
};

function SectionCard({ title, description, children }) {
  return (
    <Card className="border-border">
      <CardHeader className="border-b border-border pb-3">
        <CardTitle className="text-base tracking-tight">{title}</CardTitle>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </CardHeader>
      <CardContent className="pt-4 space-y-4">{children}</CardContent>
    </Card>
  );
}

export default function ServiceOrderNew() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { isSuperAdmin, activeTenantId, branches, activeBranch } = useTenant();
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [flows, setFlows] = useState([]);

  const tenantParam = isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {};

  useEffect(() => {
    const params = isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {};
    api.get("/service-flows", { params }).then(({ data }) => setFlows(data || [])).catch(() => setFlows([]));
  }, [isSuperAdmin, activeTenantId]);

  useEffect(() => {
    const now = new Date();
    setForm((f) => ({
      ...f,
      intake_date: f.intake_date || now.toISOString().slice(0, 10),
      intake_time: f.intake_time || now.toTimeString().slice(0, 5),
      branch_id: f.branch_id || activeBranch?.id || branches[0]?.id || "",
      received_by_user_id: f.received_by_user_id || session?.user?.id || "",
      intake_signature_name: f.intake_signature_name || session?.user?.full_name || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranch?.id]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: c }, { data: u }] = await Promise.all([
          api.get("/customers", { params: tenantParam }),
          api.get("/users", { params: tenantParam }),
        ]);
        setCustomers(c);
        setUsers(u.filter((x) => x.status === "active"));
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId, isSuperAdmin]);

  // Load vehicles when customer changes
  useEffect(() => {
    if (!form.customer_id) {
      setVehicles([]);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/vehicles", {
          params: { ...tenantParam, customer_id: form.customer_id },
        });
        setVehicles(data);
        // auto-select first vehicle if only one
        if (data.length === 1) {
          setForm((f) => ({ ...f, vehicle_id: data[0].id }));
        } else if (!data.find((v) => v.id === form.vehicle_id)) {
          setForm((f) => ({ ...f, vehicle_id: "" }));
        }
      } catch (_) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.customer_id]);

  const selectedVehicle = useMemo(
    () => vehicles.find((v) => v.id === form.vehicle_id),
    [vehicles, form.vehicle_id]
  );

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.customer_id) return toast.error("Selecciona un cliente");
    if (!form.vehicle_id) return toast.error("Selecciona un vehículo");
    if (!form.branch_id) return toast.error("Selecciona una sucursal");
    if (flows.length && !form.service_flow_id) return toast.error("Selecciona el flujo de servicio");
    setSaving(true);
    try {
      const payload = {
        ...form,
        mileage_at_entry: form.mileage_at_entry ? Number(form.mileage_at_entry) : null,
        service_flow_id: form.service_flow_id || null,
      };
      const { data } = await api.post("/service-orders", payload, { params: tenantParam });
      toast.success(`Orden ${data.folio} creada`);
      navigate(`/ordenes-de-servicio/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="page-service-order-new">
      <Button variant="ghost" size="sm" onClick={() => navigate("/ordenes-de-servicio")} data-testid="so-new-back">
        <ArrowLeft className="h-4 w-4 mr-2" /> Volver a órdenes
      </Button>

      <PageHeader
        title="Nueva orden de ingreso"
        description="Al guardar, se genera un folio único y la unidad queda registrada en el taller."
      />

      <form onSubmit={onSubmit} className="space-y-6">
        <SectionCard title="Cliente y vehículo" description="Todo folio debe estar ligado a un cliente y a un vehículo del mismo tenant.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <Select
                value={form.customer_id}
                onValueChange={(v) => setForm({ ...form, customer_id: v, vehicle_id: "" })}
              >
                <SelectTrigger data-testid="so-customer"><SelectValue placeholder="Selecciona cliente" /></SelectTrigger>
                <SelectContent>
                  {customers.length === 0 ? (
                    <SelectItem value="none" disabled>Sin clientes. Créalos en Clientes.</SelectItem>
                  ) : (
                    customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Vehículo *</Label>
              <Select
                value={form.vehicle_id}
                onValueChange={(v) => setForm({ ...form, vehicle_id: v })}
                disabled={!form.customer_id}
              >
                <SelectTrigger data-testid="so-vehicle">
                  <SelectValue placeholder={form.customer_id ? "Selecciona vehículo" : "Elige cliente primero"} />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.length === 0 ? (
                    <SelectItem value="none" disabled>Sin vehículos para este cliente</SelectItem>
                  ) : (
                    vehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.brand} {v.model} {v.year || ""} · {v.plates || "s/placas"}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          {selectedVehicle && (
            <div className="rounded-md border border-dashed border-border p-3 bg-slate-50 text-xs text-muted-foreground">
              VIN: <span className="mono">{selectedVehicle.vin || "—"}</span> · Color: {selectedVehicle.color || "—"} · Km base: {(selectedVehicle.base_mileage || 0).toLocaleString("es-MX")}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Datos de recepción">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Sucursal *</Label>
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger data-testid="so-branch"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Fecha de ingreso</Label>
              <Input
                type="date"
                value={form.intake_date}
                onChange={(e) => setForm({ ...form, intake_date: e.target.value })}
                data-testid="so-intake-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Hora</Label>
              <Input
                type="time"
                value={form.intake_time}
                onChange={(e) => setForm({ ...form, intake_time: e.target.value })}
                data-testid="so-intake-time"
              />
            </div>
            {flows.length > 0 && (
              <div className="space-y-2">
                <Label>Flujo de servicio *</Label>
                <Select value={form.service_flow_id} onValueChange={(v) => setForm({ ...form, service_flow_id: v })}>
                  <SelectTrigger data-testid="so-service-flow"><SelectValue placeholder="Mecánica, hojalatería…" /></SelectTrigger>
                  <SelectContent>
                    {flows.map((f) => <SelectItem key={f.id} value={f.id} data-testid={`so-service-flow-${f.id}`}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Tipo de servicio</Label>
              <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                <SelectTrigger data-testid="so-service-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Recibió</Label>
              <Select
                value={form.received_by_user_id}
                onValueChange={(v) => setForm({ ...form, received_by_user_id: v })}
              >
                <SelectTrigger data-testid="so-received-by"><SelectValue placeholder="Usuario" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Firma (nombre)</Label>
              <Input
                value={form.intake_signature_name}
                onChange={(e) => setForm({ ...form, intake_signature_name: e.target.value })}
                data-testid="so-signature"
                placeholder="Nombre de quien firma el ingreso"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Kilometraje al ingreso</Label>
              <Input
                type="number"
                value={form.mileage_at_entry}
                onChange={(e) => setForm({ ...form, mileage_at_entry: e.target.value })}
                data-testid="so-mileage"
                placeholder={selectedVehicle ? String(selectedVehicle.base_mileage || 0) : ""}
              />
            </div>
            <div className="space-y-2">
              <Label>Nivel de combustible</Label>
              <Select value={form.fuel_level} onValueChange={(v) => setForm({ ...form, fuel_level: v })}>
                <SelectTrigger data-testid="so-fuel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FUEL_LEVELS.map((f) => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Motivo y observaciones">
          <div className="space-y-2">
            <Label>Motivo de ingreso</Label>
            <Input
              value={form.entry_reason}
              onChange={(e) => setForm({ ...form, entry_reason: e.target.value })}
              data-testid="so-entry-reason"
              placeholder="Ej. ruido en frenos, servicio de 30k, revisión general"
            />
          </div>
          <div className="space-y-2">
            <Label>Síntomas o falla reportada por el cliente</Label>
            <Textarea
              rows={3}
              value={form.customer_reported_issue}
              onChange={(e) => setForm({ ...form, customer_reported_issue: e.target.value })}
              data-testid="so-reported-issue"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Observaciones generales</Label>
              <Textarea
                rows={3}
                value={form.general_condition_notes}
                onChange={(e) => setForm({ ...form, general_condition_notes: e.target.value })}
                data-testid="so-general-notes"
                placeholder="Golpes, rayones, condición general…"
              />
            </div>
            <div className="space-y-2">
              <Label>Accesorios</Label>
              <Textarea
                rows={3}
                value={form.accessories_notes}
                onChange={(e) => setForm({ ...form, accessories_notes: e.target.value })}
                data-testid="so-accessories"
                placeholder="Estéreo, gato, llanta de refacción, tapetes…"
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Checklist de recepción" description="Registro rápido del estado de la unidad al momento del ingreso.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Estado exterior</Label>
              <Select
                value={form.checklist.exterior_status}
                onValueChange={(v) => setForm({ ...form, checklist: { ...form.checklist, exterior_status: v } })}
              >
                <SelectTrigger data-testid="so-check-exterior"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHECKLIST_LEVEL.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Estado interior</Label>
              <Select
                value={form.checklist.interior_status}
                onValueChange={(v) => setForm({ ...form, checklist: { ...form.checklist, interior_status: v } })}
              >
                <SelectTrigger data-testid="so-check-interior"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHECKLIST_LEVEL.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Batería</Label>
              <Select
                value={form.checklist.battery_status}
                onValueChange={(v) => setForm({ ...form, checklist: { ...form.checklist, battery_status: v } })}
              >
                <SelectTrigger data-testid="so-check-battery"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BATTERY_LEVEL.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Estado de llantas</Label>
              <Select
                value={form.checklist.tire_condition}
                onValueChange={(v) => setForm({ ...form, checklist: { ...form.checklist, tire_condition: v } })}
              >
                <SelectTrigger data-testid="so-check-tires"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHECKLIST_LEVEL.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Testigos encendidos en tablero</Label>
              <Input
                value={form.checklist.dashboard_lights}
                onChange={(e) => setForm({ ...form, checklist: { ...form.checklist, dashboard_lights: e.target.value } })}
                data-testid="so-check-lights"
                placeholder="Ej. check engine, ABS, batería…"
              />
            </div>
            <div className="space-y-2">
              <Label>Accesorios presentes</Label>
              <Input
                value={form.checklist.accessories_present}
                onChange={(e) => setForm({ ...form, checklist: { ...form.checklist, accessories_present: e.target.value } })}
                data-testid="so-check-accessories"
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-md border border-border">
              <div>
                <Label htmlFor="visible_leaks">Fugas visibles</Label>
                <p className="text-xs text-muted-foreground">Aceite, refrigerante, frenos…</p>
              </div>
              <Switch
                id="visible_leaks"
                checked={form.checklist.visible_leaks}
                onCheckedChange={(v) => setForm({ ...form, checklist: { ...form.checklist, visible_leaks: v } })}
                data-testid="so-check-leaks"
              />
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Notas del checklist</Label>
              <Textarea
                rows={2}
                value={form.checklist.notes}
                onChange={(e) => setForm({ ...form, checklist: { ...form.checklist, notes: e.target.value } })}
                data-testid="so-check-notes"
              />
            </div>
          </div>
        </SectionCard>

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate("/ordenes-de-servicio")} data-testid="so-new-cancel">
            Cancelar
          </Button>
          <Button type="submit" disabled={saving} className="btn-tenant" data-testid="so-new-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ClipboardList className="h-4 w-4 mr-2" />}
            {saving ? "Creando…" : "Crear orden y generar folio"}
          </Button>
        </div>
      </form>
    </div>
  );
}
